/**
 * 业务接口主域名（httpUrl）状态中心
 *
 * 抽到独立文件的原因：避免 http.js <-> api.js 之间因为引用就绪 Promise
 * 而形成循环依赖（两边互相 import 会导致先加载的一方拿到 undefined）。
 *
 * 流程：
 *  1) 模块加载时先读 sessionStorage.activeDomain 作为缓存值
 *  2) 若有缓存 -> 立即 markReady（认为可以直接放行业务请求）
 *  3) 若无缓存 -> httpUrlReady 保持 pending，main.js 完成 domain/list + ping 后
 *     调用 setHttpUrl(newDomain) 或在不需要切换时调用 markHttpUrlReady() 解锁
 */

// 默认接口地址（CN 用户在 main.js 启动时会通过 domain/list + ping 动态替换）
// 优先读 Vue CLI 注入的环境变量 VUE_APP_DEFAULT_HTTP_URL：
//   - npm run serve      -> .env.development -> 测试域名
//   - npm run build.test -> .env.test        -> 测试域名
//   - npm run build      -> .env.production  -> 正式域名
// 未配置环境变量时回退到测试域名，保持兼容。
//
// 防御性清洗：dotenv 不支持行内 // 注释，等号右边一直到行尾都会被当成值。
// 万一 .env 写成 "https://x.com  //注释"，sanitizeUrl 强行只取首段 URL
// （第一个空白 / # 之前），避免脏字符串污染下游模板字符串和 axios 请求。
// 触发过线上 bug：脏值通过 new URL() 解析后空格被当 host 终止符，
// 最终 url 拼成 "https://正确host//%E6%B5%8B%E8%AF%95%E5%9F%9F%E5%90%8D/api/..."。
function sanitizeUrl(raw) {
  if (typeof raw !== "string") return "";
  return raw.trim().split(/[\s#]/)[0].replace(/\/+$/, "");
}

export const DEFAULT_HTTP_URL = sanitizeUrl(
  process.env.VUE_APP_DEFAULT_HTTP_URL || "https://apichat.rainshare.store"
);

let httpUrl = DEFAULT_HTTP_URL;
try {
  const cached = sessionStorage.getItem("activeDomain");
  if (cached) httpUrl = sanitizeUrl(cached);
} catch (e) {
  // ignore
}

let resolveReady;
export const httpUrlReady = new Promise((resolve) => {
  resolveReady = resolve;
});
let alreadyReady = false;
const markReady = () => {
  if (alreadyReady) return;
  alreadyReady = true;
  resolveReady && resolveReady(httpUrl);
};

// 命中缓存时直接放行（异步微任务里 resolve，确保 import 顺序无关）
if (httpUrl !== DEFAULT_HTTP_URL) {
  Promise.resolve().then(markReady);
}

export const setHttpUrl = (url) => {
  if (!url || typeof url !== "string") return;
  httpUrl = sanitizeUrl(url);
  if (!httpUrl) return;
  try {
    sessionStorage.setItem("activeDomain", httpUrl);
  } catch (e) {
    // ignore
  }
  markReady();
};

// 由 main.js 在"不需要 ping / ping 全部失败 / 非 CN 用户"这些场景下显式调用，
// 表示"不再等了，按当前 httpUrl 放行"，避免业务请求被永远卡住。
export const markHttpUrlReady = () => {
  markReady();
};

export const getHttpUrl = () => httpUrl;
